import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import dayjs from "dayjs";
import { Calendar as CalendarIcon } from "lucide-react";

import { CalendarPanel } from "@/components/common/inputs";
import { cn } from "@/utils/css";

// "Jan 11 - 18, 25" when the range is within one month, "Jan 11 - Feb 18, 25" otherwise —
// matches the format the existing `CalendarInput` uses on the loadboard search.
const formatRange = (start, end) => {
    if (start.month() === end.month() && start.year() === end.year()) {
        return `${start.format("MMM D")} - ${end.format("D, YY")}`;
    }
    return `${start.format("MMM D")} - ${end.format("MMM D, YY")}`;
};

const triggerLabel = (value, placeholder) => {
    switch (value.kind) {
        case "today":
            return "Today";
        case "single":
            return dayjs(value.start).format("MMM D, YYYY");
        case "range":
            return formatRange(dayjs(value.start), dayjs(value.end));
        default:
            return placeholder;
    }
};

// Translates the parsed value into the `value` prop the underlying `react-calendar` expects:
// a single `Date` for single-day selection, a `[Date, Date]` tuple for ranges. The "today"
// sentinel maps to today's date so the calendar highlights it on open.
const calendarValueFor = (value) => {
    switch (value.kind) {
        case "today":
            return new Date();
        case "single": {
            const d = dayjs(value.start).toDate();
            return [d, d];
        }
        case "range":
            return [dayjs(value.start).toDate(), dayjs(value.end).toDate()];
        default:
            return undefined;
    }
};

// Ready-date filter for the truck list toolbar. Renders the same pill trigger as the other
// filters, with a calendar popover that supports single-day picking, range selection (two
// clicks), and quick "Any" / "Today" presets.
//
// `value` is `{ kind: "all" | "today" | "single" | "range", start?, end? }` — the caller
// decides how to persist it (URL, Redux, etc.); the picker has no opinion. A `single` value
// is committed when the user picks the same day twice in range mode (`react-calendar` emits
// `[date, date]`), which we collapse here so the trigger reads "Jun 5, 2026" rather than
// "Jun 5 - 5, 26".
//
// The popover uses Headless UI's `anchor` prop so it portals out of the toolbar's
// `overflow-hidden` ancestor instead of being clipped by it.
export const TruckListDatePicker = ({ value, onChange, placeholder = "Ready date" }) => {
    const isEmpty = value.kind === "all";

    return (
        <Popover className="relative">
            {({ close }) => {
                const select = (next) => {
                    onChange(next);
                    close();
                };

                const handleCalendarChange = (next) => {
                    if (!next) return;
                    if (Array.isArray(next)) {
                        const [start, end] = next;
                        const startStr = dayjs(start).format("YYYY-MM-DD");
                        // `react-calendar` in range mode emits a partial selection [start, null]
                        // on the first click. That day is committed straight away so closing the
                        // popover keeps it; a second click widens it into a range and closes.
                        if (!end) {
                            onChange({ kind: "single", start: startStr, end: startStr });
                            return;
                        }
                        const endStr = dayjs(end).format("YYYY-MM-DD");
                        select(
                            startStr === endStr
                                ? { kind: "single", start: startStr, end: endStr }
                                : { kind: "range", start: startStr, end: endStr },
                        );
                        return;
                    }
                    const dateStr = dayjs(next).format("YYYY-MM-DD");
                    onChange({ kind: "single", start: dateStr, end: dateStr });
                };

                return (
                    <>
                        <PopoverButton
                            // Width matches the other toolbar pills (160px) in the placeholder
                            // state and grows beyond that to keep a full range like
                            // "Jun 1 - Jul 15, 26" visible in one line. `whitespace-nowrap` stops
                            // the label from wrapping inside the pill.
                            className="flex h-10 min-w-[160px] items-center justify-between gap-2 whitespace-nowrap rounded-[20px] border border-cm-p-outline bg-cm-p-surface-container px-4 outline-none"
                        >
                            <span
                                className={cn(
                                    "text-[15px]",
                                    isEmpty ? "font-normal text-cm-p-text-secondary" : "font-medium text-cm-p-text-primary",
                                )}
                            >
                                {triggerLabel(value, placeholder)}
                            </span>
                            <CalendarIcon className="h-4 w-4 shrink-0 text-cm-p-text-secondary" />
                        </PopoverButton>

                        <PopoverPanel
                            anchor="bottom end"
                            className="z-dropdown mt-2 w-[354px] rounded-xl border border-cm-p-outline bg-cm-p-surface shadow-[0_4px_14px_-3px_rgba(93,93,93,0.08)]"
                        >
                            <CalendarPanel
                                onChange={handleCalendarChange}
                                value={calendarValueFor(value)}
                                selectRange
                                onAny={() => select({ kind: "all" })}
                                onToday={() => select({ kind: "today" })}
                                onApply={close}
                            />
                        </PopoverPanel>
                    </>
                );
            }}
        </Popover>
    );
};
